import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, tableCellClasses, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { identifiersData, inputData, namesData } from "@/data";
import { StyledTableCell, StyledTextField } from "@/styles";
import { theme } from "@/theme";

const editorFieldWidth = 235;

const editorFieldHeight = 38;

const editorIconSize = 20;

// Matches the design's `light/ink/outline`, which is also MUI's default unfocused outline.
const editorBorderColor = "rgba(0, 0, 0, 0.23)";

const EditableTableCell = ({ disabled, isEditing, onCancel, onConfirm, rowId, value, width, onEdit }) => {
    const { t } = useTranslation();

    const [draft, setDraft] = useState(value ?? "");

    const inputRef = useRef(null);

    useEffect(
        () => {
            if (isEditing) {
                setDraft(value ?? "");

                inputRef.current?.focus();
            }
        },
        [isEditing], // eslint-disable-line
    );

    const { button: buttonId, input: inputId } = identifiersData.ids;

    const { cancel: cancelButtonName, save: saveButtonName } = namesData.buttons;

    const confirmHandler = () => onConfirm(draft.trim());

    const keyDownHandler = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();

            confirmHandler();
        }

        if (event.key === "Escape") onCancel();
    };

    return (
        <StyledTableCell
            onClick={isEditing || disabled ? undefined : onEdit}
            sx={{
                // Overrides StyledTableCell's own body-cell truncation, which would clip the input.
                // Must be nested under the same selector to win on specificity.
                [`&.${tableCellClasses.body}`]: {
                    cursor: isEditing || disabled ? "default" : "pointer",
                    maxWidth: "none",
                    overflow: "visible",
                    textOverflow: "clip",
                    width,
                },
            }}
        >
            {isEditing ? (
                <Box
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        gap: "8px",
                    }}
                >
                    <StyledTextField
                        id={`${inputData.names.dspRoleName}_${inputId}_${rowId}`}
                        inputRef={inputRef}
                        value={draft}
                        sx={{
                            // The field is autofocused, so MUI would paint the outline with the
                            // primary green; the design keeps the neutral outline while editing.
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: editorBorderColor,
                                borderWidth: 1,
                            },
                            "& .MuiInputBase-root": { height: editorFieldHeight },
                            width: editorFieldWidth,
                        }}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={keyDownHandler}
                    />
                    <Tooltip title={t("actions.save")}>
                        <IconButton
                            id={`${saveButtonName}_${buttonId}_${rowId}`}
                            sx={{ padding: 0 }}
                            onClick={confirmHandler}
                        >
                            <CheckIcon
                                sx={{
                                    color: theme.palette.success.main,
                                    fontSize: editorIconSize,
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("actions.cancel")}>
                        <IconButton
                            id={`${cancelButtonName}_${buttonId}_${rowId}`}
                            sx={{ padding: 0 }}
                            onClick={onCancel}
                        >
                            <CloseIcon
                                sx={{
                                    color: theme.palette.error.main,
                                    fontSize: editorIconSize,
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : (
                value || "-"
            )}
        </StyledTableCell>
    );
};

export default EditableTableCell;

EditableTableCell.propTypes = {
    disabled: PropTypes.bool,
    isEditing: PropTypes.bool,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    onEdit: PropTypes.func,
    rowId: PropTypes.number,
    value: PropTypes.string,
    width: PropTypes.number,
};
